import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../../api/core.js';
import { Calendar, Clock, Mail, Play, Pause, Plus, Edit, Trash2, RefreshCw, FileText } from 'lucide-react';
import ReportScheduleWizard from '../reports/ReportScheduleWizard';
import { useFormat } from '../../i18n/format.js';

const SCHEDULE_LABELS = {
  daily: 'Quotidien',
  weekly: 'Hebdomadaire',
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  yearly: 'Annuel'
};

const REPORT_TYPE_LABELS = {
  fleet_insurance: 'Assurance Flotte',
  vehicle_inventory: 'Inventaire Véhicules',
  license_validity: 'Validité Permis',
  assignment_history: 'Historique Affectations',
  tco: 'Coûts Maintenance',
  fuel_expenses: 'Profil Énergétique',
  budget_comparison: 'Suivi Budget',
  claims_status: 'Sinistres en Cours',
  downtime: 'Véhicules Immobilisés',
  breakdown_analysis: 'Analyse Interventions',
  monthly_report: 'Tableau de Bord Mensuel'
};

function ReportSubscriptionsTab({ onMessage }) {
  const { fmt } = useFormat();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [triggeringId, setTriggeringId] = useState(null);

  // Edit form state (simplified - only editable fields)
  const [editFormData, setEditFormData] = useState({
    name: '',
    recipients: '',
    schedule_hour: 8,
    schedule_minute: 0
  });

  useEffect(() => {
    loadSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/report-subscriptions', {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success || data.ok) {
        setSubscriptions(data.data || []);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      onMessage?.({ title: 'Erreur', message: 'Erreur lors du chargement des abonnements', type: 'error' });
    }
    setLoading(false);
  };

  const handleWizardSuccess = (result) => {
    onMessage?.({
      title: 'Succès',
      message: `${result.created} abonnement(s) créé(s)`,
      type: 'success'
    });
    loadSubscriptions();
  };

  const handleUpdate = async () => {
    try {
      const recipients = editFormData.recipients.split(',').map(e => e.trim()).filter(e => e);
      const payload = {
        name: editFormData.name,
        recipients,
        schedule_hour: editFormData.schedule_hour,
        schedule_minute: editFormData.schedule_minute
      };

      const response = await fetch(`/api/v1/report-subscriptions/${editingSubscription.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success || data.ok) {
        onMessage?.({ title: 'Succès', message: 'Abonnement mis à jour', type: 'success' });
        setShowEditModal(false);
        setEditingSubscription(null);
        loadSubscriptions();
      } else {
        onMessage?.({ title: 'Erreur', message: data.error || 'Erreur lors de la mise à jour', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      onMessage?.({ title: 'Erreur', message: 'Erreur lors de la mise à jour', type: 'error' });
    }
  };

  const handleToggleEnabled = async (subscription) => {
    try {
      const response = await fetch(`/api/v1/report-subscriptions/${subscription.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ is_enabled: !subscription.is_enabled })
      });
      const data = await response.json();

      if (data.success || data.ok) {
        onMessage?.({
          title: 'Succès',
          message: subscription.is_enabled ? 'Abonnement désactivé' : 'Abonnement activé',
          type: 'success'
        });
        loadSubscriptions();
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
    }
  };

  const handleDelete = async (subscription) => {
    if (!window.confirm(`Supprimer l'abonnement "${subscription.name}" ?`)) return;

    try {
      const response = await fetch(`/api/v1/report-subscriptions/${subscription.id}?hard_delete=true`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data.success || data.ok) {
        onMessage?.({ title: 'Succès', message: 'Abonnement supprimé', type: 'success' });
        loadSubscriptions();
      }
    } catch (error) {
      console.error('Error deleting subscription:', error);
    }
  };

  const handleTrigger = async (subscription) => {
    setTriggeringId(subscription.id);
    try {
      const response = await fetch(`/api/v1/report-subscriptions/${subscription.id}/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ process_now: true })
      });
      const data = await response.json();

      if (data.success || data.ok) {
        onMessage?.({
          title: 'Succès',
          message: `Rapport #${data.data?.id || 'N/A'} généré`,
          type: 'success'
        });
        loadSubscriptions();
      } else {
        onMessage?.({ title: 'Erreur', message: data.error, type: 'error' });
      }
    } catch (error) {
      console.error('Error triggering report:', error);
      onMessage?.({ title: 'Erreur', message: 'Erreur lors du déclenchement', type: 'error' });
    }
    setTriggeringId(null);
  };

  const openEditModal = (subscription) => {
    setEditingSubscription(subscription);
    setEditFormData({
      name: subscription.name || '',
      recipients: (subscription.recipients_json || []).join(', '),
      schedule_hour: subscription.schedule_hour || 8,
      schedule_minute: subscription.schedule_minute || 0
    });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Chargement des abonnements...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Rapports Programmés
        </h3>
        <div className="flex gap-2">
          <button
            onClick={loadSubscriptions}
            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center gap-1"
            title="Actualiser"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowWizard(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nouvel Abonnement
          </button>
        </div>
      </div>

      {/* Subscriptions List */}
      {subscriptions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Aucun abonnement configuré</p>
          <p className="text-sm text-gray-500 mt-1">Créez un abonnement pour recevoir des rapports automatiques</p>
          <button
            onClick={() => setShowWizard(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Créer un abonnement
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {subscriptions.map((sub) => (
            <div
              key={sub.id}
              className={`bg-white border rounded-lg p-4 transition-opacity ${!sub.is_enabled ? 'opacity-60' : ''}`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900">{sub.name}</h4>
                    {!sub.is_enabled && (
                      <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                        Désactivé
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {REPORT_TYPE_LABELS[sub.report_type] || sub.report_type}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {SCHEDULE_LABELS[sub.schedule_type] || sub.schedule_type}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {String(sub.schedule_hour || 0).padStart(2, '0')}:{String(sub.schedule_minute || 0).padStart(2, '0')}
                    </span>
                    {sub.recipients_json?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {sub.recipients_json.length} destinataire(s)
                      </span>
                    )}
                  </div>
                  {sub.last_run_at && (
                    <div className="mt-2 text-xs text-gray-500">
                      Dernier rapport: {fmt.dateTime(sub.last_run_at)}
                      {sub.report_count > 0 && ` (${sub.report_count} rapports générés)`}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleTrigger(sub)}
                    disabled={triggeringId === sub.id || !sub.is_enabled}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50 transition-colors"
                    title="Générer maintenant"
                  >
                    {triggeringId === sub.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleToggleEnabled(sub)}
                    className={`p-2 rounded-lg transition-colors ${sub.is_enabled ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
                    title={sub.is_enabled ? 'Désactiver' : 'Activer'}
                  >
                    {sub.is_enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEditModal(sub)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(sub)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Creation Wizard (unified component) */}
      <ReportScheduleWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onSuccess={handleWizardSuccess}
      />

      {/* Simple Edit Modal (for updating existing subscriptions) */}
      {showEditModal && editingSubscription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
              <h3 className="text-lg font-semibold text-white">
                Modifier l'abonnement
              </h3>
              <p className="text-blue-100 text-sm mt-1">
                {REPORT_TYPE_LABELS[editingSubscription.report_type] || editingSubscription.report_type}
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Schedule Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heure d'envoi</label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={editFormData.schedule_hour}
                    onChange={(e) => setEditFormData({ ...editFormData, schedule_hour: parseInt(e.target.value) })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[...Array(24)].map((_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}h</option>
                    ))}
                  </select>
                  <select
                    value={editFormData.schedule_minute}
                    onChange={(e) => setEditFormData({ ...editFormData, schedule_minute: parseInt(e.target.value) })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[0, 15, 30, 45].map((m) => (
                      <option key={m} value={m}>{String(m).padStart(2, '0')} min</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destinataires
                </label>
                <textarea
                  value={editFormData.recipients}
                  onChange={(e) => setEditFormData({ ...editFormData, recipients: e.target.value })}
                  placeholder="email1@example.com, email2@example.com"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">Séparez les adresses par des virgules</p>
              </div>

              {/* Info box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Fréquence:</strong> {SCHEDULE_LABELS[editingSubscription.schedule_type]}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Pour changer le type de rapport ou la fréquence, supprimez cet abonnement et créez-en un nouveau.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => { setShowEditModal(false); setEditingSubscription(null); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportSubscriptionsTab;
